import { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import flow from 'lodash/flow';
import get from 'lodash/get';
import * as PlayerActions from 'src/common/actions/player';
import { syncedPlayerActions } from 'src/common/decorators/connectWithPlayer';
import playerStatuses from 'src/common/utils/playerStatuses';
import { PAUSE_PREROLL_ENABLED } from 'src/common/constants/experiments/ads';
import cssVariables from 'src/common/styles/variables';
import CircularProgress from '../../../shared/CircularProgress';
import PopOutButton from '../../../shared/button/PopOutButton';
import connectWithExperiments from '../../../../decorators/connectWithExperiments';
import { selectAutoPlayGuideItem } from '../../../../selectors/player';
import { isMedium } from '../../../../utils/breakpoints';
import { playerStatusToSvgIconMap } from '../../../../utils/playerStatusToIconMap';

import css from './player-play-button.module.scss';

export class PlayButton extends Component {
  static propTypes = {
    playerActions: PropTypes.object.isRequired,
    playerStatus: PropTypes.string.isRequired,
    breakpoint: PropTypes.number.isRequired,
    canScrub: PropTypes.bool,
    guideItem: PropTypes.object,
    [PAUSE_PREROLL_ENABLED]: PropTypes.bool.isRequired,
    externalHtmlPlayerUrl: PropTypes.string,
    className: PropTypes.string,
    customHeight: PropTypes.number,
    customWidth: PropTypes.number,
    customFill: PropTypes.string,
    progressColor: PropTypes.string,
  };

  static defaultProps = {
    className: '',
  };

  handleClick = () => {
    const { playerStatus, canScrub, playerActions, guideItem } = this.props;
    const playerAction = syncedPlayerActions[playerStatus];

    if (playerAction) {
      playerAction(playerActions, canScrub, guideItem);
    }
  };

  render() {
    const {
      playerStatus,
      canScrub,
      externalHtmlPlayerUrl,
      [PAUSE_PREROLL_ENABLED]: isPausePrerollEnabled,
      className,
      breakpoint,
      customHeight,
      customWidth,
      customFill,
      progressColor,
    } = this.props;

    const isMediumBreakpoint = isMedium(breakpoint);
    const predeterminedHeight = isMediumBreakpoint ? 45 : 40;
    const predeterminedWidth = isMediumBreakpoint ? 45 : 40;

    const height = customHeight || predeterminedHeight;
    const width = customWidth || predeterminedWidth;

    const shouldShowInPrerollStatus = isPausePrerollEnabled
      ? false
      : playerStatus === playerStatuses.preroll;

    if (
      playerStatus === playerStatuses.connecting ||
      shouldShowInPrerollStatus
    ) {
      return (
        <CircularProgress
          id="playerLoadingSpinner"
          className={className}
          data-testid={`player-status-${playerStatus}`}
          size={width}
          color={progressColor || cssVariables['--secondary-color-1']}
          thickness={2}
        />
      );
    }

    if (playerStatus === playerStatuses.popout) {
      return (
        <div className={className} data-testid="popoutButtonWrapper">
          <PopOutButton
            externalHtmlPlayerUrl={externalHtmlPlayerUrl}
            dataTestId={`player-status-${playerStatus}`}
            width={`${width}`}
            height={`${height}`}
          />
        </div>
      );
    }

    const PlayButtonStateRepresentation =
      playerStatusToSvgIconMap[playerStatus](canScrub);

    return (
      <div className={className} data-testid="playButtonWrapper">
        <PlayButtonStateRepresentation
          id="playerActionButton"
          dataTestId={`player-status-${playerStatus}`}
          className={css.playerPlayButton}
          onClick={this.handleClick}
          fillButtonBackground={customFill}
          height={`${height}`}
          width={`${width}`}
        />
      </div>
    );
  }
}

export function mapStateToProps(state) {
  const { player, profiles } = state;
  const { playerStatus, canScrub, externalHtmlPlayerUrl, nowPlaying } = player;
  const primaryGuideId = get(nowPlaying, 'primaryGuideId');
  const guideItem = profiles[primaryGuideId] || selectAutoPlayGuideItem(state);

  return {
    playerStatus,
    canScrub,
    externalHtmlPlayerUrl,
    guideItem,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    playerActions: bindActionCreators(PlayerActions, dispatch),
  };
}

export default flow(
  connectWithExperiments([PAUSE_PREROLL_ENABLED]),
  connect(mapStateToProps, mapDispatchToProps),
)(PlayButton);
